import {
  Box,
  Button,
  Divider,
  Typography,
  alpha,
  makeStyles,
} from '@material-ui/core';
import { FileCopy as FileCopyIcon } from '@material-ui/icons';
import copy from 'copy-to-clipboard';
import { useModal } from 'react-modal-hook';

import { ReactComponent as PixIcon } from 'assets/images/pix.svg';
import { useSearchQuery } from 'contexts/SearchQueryContext';
import { WppTemplateData } from 'store/reducers/templates';
import { centsToBrl } from 'utils/currencyUtils';
import parseTextToHtml from 'utils/parseTextToHtml';

import ModalPixCopied from './ModalPixCopied';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
  },
  messageContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  bodyText: {
    fontSize: theme.typography.body2.fontSize,
  },
  button: {
    color: 'inherit',
  },
  divider: {
    marginLeft: -16,
    marginRight: -16,
    backgroundColor: `${alpha(theme.palette.text.secondary, 0.3)}`,
  },
  lastButton: {
    marginBottom: -6, // 2px faz toda diferença
  },

  headerPix: (props: { fromMe: boolean }) => {
    const isDark = theme.palette.type === 'dark';

    return {
      backgroundColor: props.fromMe
        ? alpha(theme.palette.background.default, isDark ? 0.4 : 0.1)
        : alpha(theme.palette.background.default, 0.5),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      borderRadius: theme.spacing(1),
      marginLeft: -4,
      marginTop: -4,
      marginRight: -4,
    };
  },
  paymentMethod: (props: { fromMe: boolean }) => {
    const isDark = theme.palette.type === 'dark';

    return {
      display: 'flex',
      padding: theme.spacing(1),
      borderBottomLeftRadius: theme.spacing(1),
      borderBottomRightRadius: theme.spacing(1),
      backgroundColor: props.fromMe
        ? alpha(theme.palette.background.default, isDark ? 0.4 : 0.1)
        : alpha(theme.palette.background.default, 0.8),
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(0.5),
    };
  },
}));

interface TemplateMessagePixProps {
  template: WppTemplateData;
  fromMe?: boolean;
}

export default function TemplateMessagePix({
  template,
  fromMe = false,
}: TemplateMessagePixProps) {
  const classes = useStyles({ fromMe });
  const searchQuery = useSearchQuery();
  const components = template?.components || [];
  const body = components.find(c => c.type === 'BODY');

  const [showModalPixCopied, hideModalPixCopied] = useModal(
    ({ in: open }) => (
      <ModalPixCopied
        open={open}
        onClose={hideModalPixCopied}
        orderDetails={template?.data?.orderDetails}
      />
    ),
    [template?.data?.orderDetails],
  );

  const pixCode = template.data?.orderDetails?.pixDynamicCode?.code;
  const priceInCents = template.data?.orderDetails?.priceInCents;

  return (
    <Box className={classes.content}>
      <Box className={classes.messageContainer}>
        <Box className={classes.headerPix}>
          <Typography
            style={{
              fontWeight: 600,
              textAlign: 'center',
              padding: '8px',
            }}
            noWrap
            variant="h6"
          >
            {centsToBrl(priceInCents || 0)}
          </Typography>

          <Box className={classes.paymentMethod}>
            <Typography style={{ fontWeight: 500 }} variant="body2">
              Pagar com
            </Typography>

            <Box
              style={{
                backgroundColor: 'white',
                padding: 4,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PixIcon width={24} height={24} />
            </Box>
          </Box>
        </Box>

        {body && (
          <Typography
            className={classes.bodyText}
            dangerouslySetInnerHTML={{
              __html: parseTextToHtml(body.text || '', searchQuery),
            }}
          />
        )}

        <Box>
          <Divider className={classes.divider} />

          <Box p={0.2} className={classes.lastButton}>
            <Button
              className={classes.button}
              fullWidth
              size="small"
              startIcon={<FileCopyIcon />}
              onClick={() => {
                copy(pixCode || '');
                showModalPixCopied();
              }}
            >
              Copiar código Pix
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
